import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Copy, Loader2 } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, DemoBadge } from "@/components/billing/StatusBadge";

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = new Date(to).getTime() - now;
  if (!to || ms <= 0) return <span className="text-red-300">expired</span>;
  const m = Math.floor(ms / 60000), s = Math.floor((ms % 60000) / 1000);
  return <span data-testid="checkout-countdown">{m}:{String(s).padStart(2, "0")}</span>;
}

function CopyRow({ label, value, testid }) {
  return (
    <div className="py-3 border-b border-white/5">
      <p className="text-[10px] font-mono-cl uppercase tracking-[0.16em] text-zinc-500">{label}</p>
      <div className="flex items-start gap-2 mt-1">
        <p data-testid={testid} className="font-mono-cl text-sm break-all flex-1">{value}</p>
        <button data-testid={`${testid}-copy`} onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
          className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function PaymentPanel({ order, onSimulate }) {
  const done = order.status === "PAID";
  return (
    <div data-testid="payment-panel" className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <StatusBadge status={order.status} />
        {order.is_demo && <DemoBadge />}
        {!done && order.expires_at && <p className="text-xs text-zinc-400">Invoice expires in <Countdown to={order.expires_at} /></p>}
      </div>
      {done ? (
        <div className="py-10 text-center" data-testid="payment-success">
          <CheckCircle2 className="w-12 h-12 text-[#ccff00] mx-auto" />
          <p className="font-display text-2xl font-bold mt-3">Payment confirmed</p>
          <p className="text-zinc-400 text-sm mt-1">Your access is active.</p>
          <Link to="/account?tab=billing" className="inline-block mt-5 px-6 py-3 rounded-full bg-[#ccff00] text-black text-sm font-semibold">View my memberships</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-[180px_1fr] gap-6 mt-5">
          <div className="bg-white p-3 rounded-xl self-start w-[180px]" data-testid="payment-qr"><QRCodeSVG value={order.pay_address || ""} size={156} /></div>
          <div>
            <CopyRow label={`Send exactly (${order.pay_currency?.toUpperCase()})`} value={order.pay_amount} testid="payment-amount" />
            <CopyRow label={`Deposit address · ${order.network_name}`} value={order.pay_address} testid="payment-address" />
            {order.pay_extra_id && <CopyRow label="Memo / extra ID" value={order.pay_extra_id} testid="payment-extra-id" />}
            <p className="text-xs text-zinc-500 mt-3">Payment ID <span className="font-mono-cl" data-testid="payment-id">{order.provider_payment_id}</span></p>
          </div>
        </div>
      )}
      {!done && (
        <div className="mt-5 flex gap-2 items-start rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-200" data-testid="network-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Send only USDT on {order.network_name}. Sending another token or using another network can lose your funds. Exchanges may deduct a fee, so make sure the exact amount arrives.
        </div>
      )}
      {order.is_demo && !done && (
        <div className="mt-5 border-t border-white/10 pt-4">
          <p className="text-[10px] font-mono-cl uppercase tracking-[0.16em] text-zinc-500 mb-2">Demo simulator (no real funds)</p>
          <div className="flex flex-wrap gap-2">
            {["confirming", "partially_paid", "finished", "expired", "failed"].map((s) => (
              <button key={s} data-testid={`demo-sim-${s}`} onClick={() => onSimulate(s)} className="px-3 py-1.5 rounded-full border border-white/15 text-xs hover:border-[#ccff00]/50">{s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Checkout() {
  const { productId } = useParams();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(undefined);
  const [config, setConfig] = useState(null);
  const [network, setNetwork] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate(`/login?next=/checkout/${productId}`); }, [loading, user, navigate, productId]);
  useEffect(() => {
    client.get("/billing/config").then((r) => setConfig(r.data));
    client.get("/billing/products").then((r) => setProduct(r.data.find((p) => p.id === productId) || null));
    const oid = params.get("order");
    if (oid) client.get(`/billing/orders/${oid}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [productId, params]);
  useEffect(() => {
    if (!order || !["CREATED", "AWAITING_PAYMENT", "CONFIRMING", "PARTIALLY_PAID"].includes(order.status)) return;
    const t = setInterval(() => client.get(`/billing/orders/${order.id}`).then((r) => setOrder(r.data)), 8000);
    return () => clearInterval(t);
  }, [order]);

  const nets = (config?.networks || []).filter((n) => product?.networks?.includes(n.code));
  const start = async () => {
    setBusy(true);
    try { setOrder((await client.post("/billing/checkout", { product_id: productId, network })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const simulate = async (status) => {
    try { setOrder((await client.post(`/billing/demo/orders/${order.id}/simulate`, { status })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (product === null) return <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="checkout-unavailable"><h1 className="font-display text-3xl font-bold">Plan unavailable</h1><Link to="/membership" className="text-[#ccff00] text-sm mt-3 inline-block">See all plans</Link></div>;
  if (!product || !config) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-[1fr_1.3fr] gap-10" data-testid="checkout-page">
      <div>
        <p className="font-mono-cl text-[11px] tracking-[0.24em] uppercase text-[#ccff00]">Checkout</p>
        <h1 className="font-display text-4xl font-black mt-2" data-testid="checkout-product-name">{product.name}</h1>
        {product.analyst && <p className="text-zinc-400 mt-1">Analyst: {product.analyst.name}</p>}
        <p className="text-zinc-300 mt-4 text-sm leading-relaxed">{product.description}</p>
        <div className="mt-6 rounded-2xl border border-white/10 p-5">
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Price</span><b data-testid="checkout-price">${product.price_usd} USD</b></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Access</span><span>{product.period_days} days</span></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-zinc-400">Paid in</span><span>USDT</span></div>
          <p className="text-[11px] text-zinc-500 mt-4">One-time payment for a fixed period. Nothing renews automatically; we'll remind you before it ends.{product.refund_policy_url && <> <a href={product.refund_policy_url} className="underline">Refund policy</a>.</>}</p>
        </div>
        {product.benefits?.length > 0 && <ul className="mt-5 space-y-1.5 text-sm text-zinc-300">{product.benefits.map((b) => <li key={b}>· {b}</li>)}</ul>}
      </div>
      <div>
        {config.usdt_mode === "disabled" ? (
          <p data-testid="checkout-disabled" className="rounded-2xl border border-white/10 p-6 text-zinc-400">USDT checkout is currently unavailable.</p>
        ) : order ? <PaymentPanel order={order} onSimulate={simulate} /> : (
          <div className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-6">
            {config.usdt_mode === "demo" && <div className="mb-4"><DemoBadge /><p className="text-xs text-zinc-500 mt-2">This preview uses simulated payments. No real USDT will be requested.</p></div>}
            <p className="text-sm font-semibold mb-3">Choose a USDT network</p>
            <div className="grid gap-2">
              {nets.map((n) => (
                <button key={n.code} data-testid={`network-${n.code}`} onClick={() => setNetwork(n.code)}
                  className={`text-left px-4 py-3 rounded-xl border text-sm transition-colors ${network === n.code ? "border-[#ccff00] bg-[#ccff00]/5" : "border-white/10 hover:border-white/30"}`}>USDT · {n.name}</button>
              ))}
              {nets.length === 0 && <p className="text-sm text-zinc-500" data-testid="checkout-no-networks">No network is available for this plan right now.</p>}
            </div>
            <button data-testid="checkout-start" disabled={!network || busy} onClick={start}
              className="w-full mt-5 py-3 rounded-full bg-[#ccff00] text-black font-semibold text-sm disabled:opacity-50">{busy ? "Creating invoice…" : "Get payment details"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
